import React, { Fragment, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Components
import Button from '../../button/button';
import GenericModal from '../../generic-modal/generic-modal';
import GroupModal from '../group-modals/group-modal';

// Context
import CourseInformationContext from '../context/course-information-context';

// Helpers
import { getLeftParentModalPosition, resetGroupSectionScroll } from '../../../helpers/utils';
import { DEFAULT_GROUP_MODAL_ID } from '../../../helpers/enums';

function NewGroupItem(props) {
    // Props
    const {
        showModal,
        currentGroupId,
        groupModalVisibility,
        groupModalId,
        productId,
        newGroupHandler,
        isBtnDisabled,
        showNewCourses,
        newCoursesAvailabilityCriteria
    } = props;

    // State
    const { i18n } = useLocalisation();
    const newButtonRef = useRef(null);
    const [modalLeftPosition, setModalLeftPosition] = useState(-1);
    // Using right to avoid horizontal scroll in IE. BA 2020-01-14
    // https://stackoverflow.com/questions/32555354/css-overflowing-when-using-translate-in-ie
    const modalStyles = { right: `calc(100% - ${modalLeftPosition}px)` };

    // Context Variables
    const {
        // Select Options
        // - Country
        setCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        // - Year
        setYearHandler,
        yearSelectValue,
        yearSelectOptions,
        // - Course
        setCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        // - UPF Course
        setUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        // New Course
        setNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions
    } = useContext(CourseInformationContext);

    // Handlers
    const showCurrentEditGroupModal = () => {
        const leftPosition = getLeftParentModalPosition(newButtonRef);
        setModalLeftPosition(leftPosition);
        showModal({ modalId: currentGroupId, setEditMode: true });
    };
    const closeHandler = () => {
        resetGroupSectionScroll();
        setCountryHandler('');
        setYearHandler('');
        showModal({ modalId: DEFAULT_GROUP_MODAL_ID, isClosing: true });
    };
    const saveHandler = async ({ name }) => {
        const newGroupResponse = await newGroupHandler({ name });
        const {
            value,
            callback
        } = newGroupResponse;

        if (value === true) {
            await closeHandler();
            if (callback) {
                callback();
            }
        }

        return value;
    };
    const localSetCountryHandler = (e) => {
        const value = e.target.value;
        setCountryHandler(value);
    };
    const localSetYearHandler = (e) => {
        const value = e.target.value;
        setYearHandler(value);
    };
    const localSetCourseHandler = (e) => {
        const value = e.target.value;
        setCourseHandler(value);
    };
    const localSetUPFCourseHandler = (e) => {
        const value = e.target.value;
        setUPFCourseHandler(value);
    };
    const localSetNewCourseHandler = (e) => {
        const value = e.target.value;
        setNewCourseHandler(value);
    };

    // Course Specific props to handle the data/display
    const courseSpecificProps = {
        // Countries
        setCountryHandler: localSetCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        // Years/Grades
        setYearHandler: localSetYearHandler,
        yearSelectValue,
        yearSelectOptions,
        // Courses
        setCourseHandler: localSetCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        // New Courses i.e. conquest courses
        setNewCourseHandler: localSetNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions,
        // - UPF Course
        setUPFCourseHandler: localSetUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions
    };

    return (
        <Fragment>
            <div className="groups__new-group-container">
                <Button
                    ref={newButtonRef}
                    buttonStyle="iconText"
                    visibleText={<Trans id={'Groups - New group'} />}
                    importedClasses="button--new-group button--tertiary-cool"
                    iconName="plus"
                    ariaProps={isBtnDisabled ?
                        { title: i18n._(t`Groups - New group - disabled`) } : null
                    }
                    isBtnDisabled={isBtnDisabled}
                    callback={showCurrentEditGroupModal}
                />
                {
                    currentGroupId === groupModalId && groupModalVisibility ? (
                        <GenericModal
                            title={<Trans id="Group Modal - new - title" />}
                            closeHandler={closeHandler}
                            containerStyle={modalStyles}
                            parentClasses="modal--edit-groups">
                            <GroupModal
                                inputId={`new-group-input${currentGroupId}`}
                                initialName=""
                                currentId={currentGroupId}
                                productId={productId}
                                saveHandler={saveHandler}
                                closeHandler={closeHandler}
                                hasEdited={false} // default value
                                showNewCourses={showNewCourses}
                                newCoursesAvailabilityCriteria={newCoursesAvailabilityCriteria}
                                {...courseSpecificProps}
                            />
                        </GenericModal>
                    ) : ''
                }
            </div>
        </Fragment>
    );
}

NewGroupItem.propTypes = {
    showModal: PropTypes.func,
    newGroupHandler: PropTypes.func,
    currentGroupId: PropTypes.number,
    groupModalVisibility: PropTypes.bool,
    groupModalId: PropTypes.number,
    productId: PropTypes.number,
    isBtnDisabled: PropTypes.bool,
    showNewCourses: PropTypes.bool,
    newCoursesAvailabilityCriteria: PropTypes.object
};

export default NewGroupItem;



// WEBPACK FOOTER //
// ./components/groups/item/new-group-item.js